import { makeStyles } from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { media } from 'styled-bootstrap-grid'
import ReactPhoneInput from '@components/form/ReactPhoneInput/ReactPhoneInput'
// countryOptions feeds the retired country dropdown below
import { experienceOptions, stageOptions } from '../content'
import { accent, actionVars, body as bodyColor, Button, Lead, Pill, surface } from '../styled'

// MUI's Dialog supplies the scrim, portal, focus trap, scroll lock and Escape
// handling, matching the other modals in the app; only the content is ours.
// below the 600px paper plus its gutters, the body-scrolled paper keeps the same 12px side gutter as before
const useMuiStyles = makeStyles(() => ({
  paper: {
    borderRadius: 24,
    margin: 12,
    width: '100%',
    '@media (max-width: 623px)': {
      '&.MuiDialog-paperScrollBody': { maxWidth: 'calc(100% - 24px)' },
    },
  },
}))

// iOS Safari ignores the overflow: hidden MUI puts on the body, so the page scrolls under the dialog; pinning the body stops it
const usePinnedBody = (pinned) => {
  useEffect(() => {
    if (!pinned) return undefined
    const { style } = document.body
    const scrollY = window.scrollY
    const previous = {
      position: style.position,
      top: style.top,
      left: style.left,
      right: style.right,
      width: style.width,
    }
    const path = window.location.pathname
    Object.assign(style, { position: 'fixed', top: `-${scrollY}px`, left: 0, right: 0, width: '100%' })
    return () => {
      Object.assign(style, previous)
      // the page's own scroll comes back when the modal closes; after a submit that moved to checkout the modal
      // unmounts on the new page, which would otherwise land where the old page was scrolled to
      if (window.location.pathname === path) window.scrollTo(0, scrollY)
    }
  }, [pinned])
}

// the dialog renders in a portal outside PageRoot, so it sets the action colour itself
const Content = styled.div`
  position: relative;
  padding: 24px;
  ${(props) => props.$action && actionVars}

  ${media.sm`padding: 40px;`}
`

const CloseButton = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  background: ${surface};
  border: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  font-size: 16px;
  cursor: pointer;
  color: ${bodyColor};
`

const Title = styled.h3`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0 0 8px;
`

// One column on phones; email and phone share a row from `sm` up.
const Fields = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin-bottom: 10px;

  ${media.sm`grid-template-columns: 1fr 1fr;`}

  > * {
    min-width: 0;
    width: 100%;
  }
`

const fieldStyles = `
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #dddddd;
  font-family: inherit;
  color: #333333;
  background: #ffffff;
  /* 16px stops iOS Safari zooming the viewport when a field takes focus */
  font-size: 16px;
`

const Input = styled.input`
  ${fieldStyles}
  grid-column: 1 / -1;

  ${media.sm`grid-column: ${(props) => (props.$half ? 'auto' : '1 / -1')};`}
`

const Select = styled.select`
  ${fieldStyles}
  width: 100%;
  grid-column: 1 / -1;

  /* Replace the native chevron: it renders inconsistently and hugs the edge */
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666666' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  padding-right: 44px;
`

// Hosts the shared react-phone-input-2 widget inside the modal's field grid,
// restyled to sit flush with the other inputs
const PhoneField = styled.div`
  grid-column: 1 / -1;

  ${media.sm`grid-column: auto;`}

  .react-tel-input .form-control {
    width: 100%;
    height: 50px;
    border-radius: 12px;
    border: 1px solid #dddddd;
    color: #333333;
    font-family: inherit;
    /* 16px stops iOS Safari zooming the viewport when the field takes focus */
    font-size: 16px;
  }

  .react-tel-input .flag-dropdown {
    border: 1px solid #dddddd;
    border-radius: 12px 0 0 12px;
    background: #ffffff;
  }
`

const Field = styled.div`
  margin-top: ${(props) => props.$gap || 16}px;
`

const Label = styled.label`
  font-size: 14.5px;
  font-weight: 700;
  margin-bottom: 6px;
  display: block;
`

const Success = styled.div`
  text-align: center;
  padding: 24px 0;
`

const Tick = styled.div`
  font-size: 40px;
  margin-bottom: 16px;
  color: ${accent};
`

const ErrorMessage = styled.p`
  color: #c0392b;
  font-size: 14px;
  margin: 12px 0 0;
`

const FieldError = styled.p`
  color: #c0392b;
  font-size: 13px;
  margin: 6px 0 0;
  grid-column: 1 / -1;
`

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i

const validate = (values, askProfile) => {
  const errors = {}
  if (values.fullName.trim().split(/\s+/).length < 2) errors.fullName = 'Please enter your first and last name.'
  if (!emailPattern.test(values.email.trim())) errors.email = 'Please enter a valid email address.'
  if (!/^\d{10,15}$/.test(values.phone)) errors.phone = 'Please enter a valid phone number.'
  if (askProfile && !values.experience) errors.experience = 'Please choose your experience level.'
  if (askProfile && !values.stage) errors.stage = 'Please choose your current stage.'
  return errors
}

const TermsNote = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
  color: ${bodyColor};

  a {
    text-decoration: underline;
  }
`

// country and countryCode mirror the phone widget's flag selector, which starts on US; the code is what's stored
const EMPTY = {
  fullName: '',
  email: '',
  phone: '',
  country: 'United States',
  countryCode: 'US',
  experience: '',
  stage: '',
}

/**
 * Shared intake / waitlist modal for the three program pages.
 *
 * @param onSubmit  optional async handler receiving the form values. Wire it to the
 *                  intake endpoint; without it the modal just shows the
 *                  confirmation state, matching the original static design.
 *                  Return `false` to skip the confirmation state (e.g. when the
 *                  handler redirects to checkout instead).
 * @param collectAccountFields  continuing accepts the Terms of Use, for flows that
 *                  create an account (the Jumpstart purchase). No password —
 *                  accounts are passwordless and receive a sign-in link by email.
 * @param askProfile  ask the experience and stage questions. Jumpstart turns them
 *                  off: the portal's day-one assessment asks both.
 * @param action    use the ad page's action colour on the buttons.
 */
const IntakeModal = ({
  open,
  onClose,
  eyebrow,
  title,
  intro,
  submitLabel,
  successTitle,
  successBody,
  onSubmit,
  collectAccountFields,
  askProfile = true,
  action,
}) => {
  const muiClasses = useMuiStyles()
  usePinnedBody(open)
  const [values, setValues] = useState(EMPTY)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [successOverride, setSuccessOverride] = useState(null)
  const [touched, setTouched] = useState({})
  const [attempted, setAttempted] = useState(false)

  useEffect(() => {
    if (open) {
      setSubmitted(false)
      setSubmitting(false)
      setError(null)
      setSuccessOverride(null)
      setValues(EMPTY)
      setTouched({})
      setAttempted(false)
    }
  }, [open])

  const setValue = useCallback(
    (field) => (event) => setValues((current) => ({ ...current, [field]: event.target.value })),
    [],
  )

  const touch = useCallback((field) => () => setTouched((current) => ({ ...current, [field]: true })), [])

  const errors = validate(values, askProfile)
  const showError = (field) => (attempted || touched[field]) && errors[field]

  const handleSubmit = async (event) => {
    event.preventDefault()
    setAttempted(true)
    if (Object.keys(errors).length) return

    setError(null)
    setSubmitting(true)

    try {
      const result = onSubmit ? await onSubmit({ ...values, acceptedTerms: !!collectAccountFields }) : undefined
      if (result !== false) {
        // The handler may override the confirmation copy, e.g. "welcome back,
        // check your email" when an existing account gets a sign-in link
        if (result?.successTitle || result?.successBody) setSuccessOverride(result)
        setSubmitted(true)
      }
    } catch (submitError) {
      setError(submitError?.message || 'Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    // scroll="body": iOS Safari still scrolls the page behind a fixed dialog, which leaves the caret floating off the focused field
    <Dialog
      open={open}
      onClose={onClose}
      scroll="body"
      maxWidth="sm"
      fullWidth
      classes={{ paper: muiClasses.paper }}
      aria-label={title}
    >
      <Content $action={action}>
        <CloseButton type="button" onClick={onClose} aria-label="Close">
          &times;
        </CloseButton>

        {submitted ? (
          <Success>
            <Tick>&#10003;</Tick>
            <Title>{successOverride?.successTitle || successTitle}</Title>
            <Lead $size="15px" $margin="0 0 24px">
              {successOverride?.successBody || successBody}
            </Lead>
            <Button type="button" $variant="dark" onClick={onClose}>
              Close
            </Button>
          </Success>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <Pill $gap={16}>{eyebrow}</Pill>
            <Title>{title}</Title>
            <Lead $size="14.5px" $margin="0 0 24px">
              {intro}
            </Lead>

            <Fields>
              <Input
                type="text"
                placeholder="Full name"
                value={values.fullName}
                onChange={setValue('fullName')}
                onBlur={touch('fullName')}
                required
              />
              {showError('fullName') && <FieldError>{errors.fullName}</FieldError>}
              <Input
                $half
                type="email"
                placeholder="Email"
                value={values.email}
                onChange={setValue('email')}
                onBlur={touch('email')}
                required
              />
              <PhoneField>
                <ReactPhoneInput
                  value={values.phone}
                  inputProps={{ required: true, onBlur: touch('phone') }}
                  onChange={(phone, countryData) =>
                    setValues((current) => ({
                      ...current,
                      phone,
                      country: countryData?.name || current.country,
                      countryCode: countryData?.countryCode
                        ? countryData.countryCode.toUpperCase()
                        : current.countryCode,
                    }))
                  }
                />
              </PhoneField>
              {showError('email') && <FieldError>{errors.email}</FieldError>}
              {showError('phone') && <FieldError>{errors.phone}</FieldError>}
              {/* Country comes from the phone widget's flag selector; the
                  dedicated dropdown is retired. Restore by uncommenting. */}
              {/* <Select value={values.country} onChange={setValue('country')} required>
                <option value="">Country</option>
                {countryOptions.map((country) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </Select> */}
            </Fields>

            {askProfile && (
              <>
                <Field $gap={18}>
                  <Label htmlFor="es-experience">1. What is your entrepreneurship experience level?</Label>
                  <Select
                    id="es-experience"
                    value={values.experience}
                    onChange={setValue('experience')}
                    onBlur={touch('experience')}
                    required
                  >
                    <option value="">Choose the one that fits best right now</option>
                    {experienceOptions.map((option) => (
                      <option key={option.key} value={option.value}>
                        {option.key}. {option.label}
                      </option>
                    ))}
                  </Select>
                  {showError('experience') && <FieldError>{errors.experience}</FieldError>}
                </Field>

                <Field>
                  <Label htmlFor="es-stage">2. What stage are you at right now?</Label>
                  <Select
                    id="es-stage"
                    value={values.stage}
                    onChange={setValue('stage')}
                    onBlur={touch('stage')}
                    required
                  >
                    <option value="">Pick where your current idea or business sits today</option>
                    {stageOptions.map((option) => (
                      <option key={option.key} value={option.value}>
                        {option.key}. {option.label}
                      </option>
                    ))}
                  </Select>
                  {showError('stage') && <FieldError>{errors.stage}</FieldError>}
                </Field>
              </>
            )}

            {/* The terms checkbox is retired: continuing accepts them, per the note under the button.
                Restore with an acceptedTerms state, this checkbox and a `terms` check in validate. */}
            {/* <TermsLabel>
              <input type="checkbox" checked={acceptedTerms} onChange={onTermsChange} required />
              <span>
                I accept the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Use</a>
              </span>
            </TermsLabel> */}

            <Button type="submit" $block $gap={22} disabled={submitting}>
              {submitting ? 'One moment…' : submitLabel}
            </Button>

            {collectAccountFields && (
              <TermsNote>
                By continuing, you accept our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  Terms of Use
                </a>
                .
              </TermsNote>
            )}

            {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
          </form>
        )}
      </Content>
    </Dialog>
  )
}

export default IntakeModal
